import React, { Component } from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import isEqual from 'lodash/isEqual';

// components
import PrescriptionsFlags from 'manage/PrescriptionsFlags';
import OptionsMenu from 'manage/OptionsMenu';
import ApplicationRateThumbnail from 'dataviz/ApplicationRateThumbnail';
import Name from 'manage/Name';
import Timestamp from 'manage/Timestamp';
import ApplicationSummary from 'manage/ApplicationSummary';
import { ExpandingSection } from '@tcc/crate';
import ApplicationScriptCardProductList from './ApplicationScriptCardProductList';

// Selectors
import { getActivityByUuid, getActivityCategoriesByActivityType } from 'activities/selectors';
import { getActiveScriptId, scriptOptionsIsOpenForScriptId, moreDetailsIsOpenForScriptId } from 'current/selectors';
import { canManualScript } from 'product-features/selectors';
import {
  getActivityTypeForScript,
  isAdvancedScript,
  isDisplayPrimaryScript
} from 'scripts/selectors';
import {
  getManageApplicationSummary
} from 'scripts/application-selectors';
import {
  getProductValidationValues
} from 'user/selectors';

// Actions
import { setScriptOptionsIsOpen, setMoreDetailsIsOpen } from 'current/uiwidgets/';
import { editScript } from 'scripts';
import { requestDriveDevices, requestPreferredDevice } from 'devices/requesters';
import { requestAffiliateList } from 'affiliates/requesters';

import { getMessage } from 'util/locale';

const baseString = 'vra-prescription-card';

import { trackAction } from 'util/analytics';

class ApplicationScriptCardContainer extends Component {

  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  moreDetailsExpanded = true;
  setMoreDetailsExpanded(newValue) {
    this.moreDetailsExpanded = newValue;
  }

  render() {
    const {
      activity,
      activityCategory,
      canManualScript,
      applicationSummary,
      isSelected,
      moreDetailsIsOpen,
      onClickEditTrigger,
      onClickScriptOptionsTrigger,
      onClickToggleDetailsTrigger,
      onSelectScript,
      productValidationValues,
      script,
      scriptId,
      scriptOptionsIsOpen,
      showPrimaryFlag,
      onHideCallbackOptionsTrigger
    } = this.props;

    const classString = classNames({
      [`${baseString}`]: true,
      'isSelected': isSelected,
      [`${baseString}--manual-application`]: true
    });
    const dataTestId = showPrimaryFlag ? 'script-primary' : 'script-not-primary';

    return (
      <div
        className={classString}
        data-test-id={baseString}
        key={scriptId}
        onClick={() => {
          trackAction('Clicked on a protection script on the left panel', {scriptId});
          onSelectScript(scriptId);
        }}
      >
        <PrescriptionsFlags
          createdAt={script['create-time']}
          isPrimary={showPrimaryFlag}
          program={script.attributes?.program}
        />
        <div className={`${baseString}__identification-section`}>
          <ApplicationRateThumbnail productValidationValues={productValidationValues} script={script} />
          <div className={`${baseString}__name-section`}>
            <Name
              canManualScript={canManualScript}
              isSelected={isSelected}
              name={script.name}
              onClickEditTrigger={onClickEditTrigger}
              scriptId={scriptId}
              testId="script-application-name"
            />
            <Timestamp timestamp={script['update-time']} />
          </div>
          <OptionsMenu
            onClick={() => onClickScriptOptionsTrigger({ scriptId, currentStatus: scriptOptionsIsOpen })}
            onHideCallback={() => onHideCallbackOptionsTrigger({ scriptId })}
            scriptOptionsIsOpen={scriptOptionsIsOpen}
          />
        </div>
        <section className="vra-prescription-card__content-wrapper" data-test-id={dataTestId}>
          <ApplicationSummary activity={activity} activityCategory={activityCategory} summary={applicationSummary} />
          <section className="vra-prescription-card__expanding-section">
            <ExpandingSection
              className="center-aligned-expanding-section"
              expanded={moreDetailsIsOpen}
              onClick={() => onClickToggleDetailsTrigger({ scriptId, currentStatus: moreDetailsIsOpen })}
              testId="-customers-without-recs-expanding-section"
              title={getMessage(moreDetailsIsOpen ? 'label-text__fewer-details' : 'label-text__more-details')}
            >
              <ApplicationScriptCardProductList
                applicationProducts={script.meta.products}
              />
            </ExpandingSection>
          </section>
        </section>
      </div>
    );
  }
}

ApplicationScriptCardContainer.propTypes = {
  activity: propTypes.object,
  activityCategory: propTypes.object.isRequired,
  activityType: propTypes.string,
  applicationSummary: propTypes.arrayOf(propTypes.object),
  canManualScript: propTypes.bool.isRequired,
  isAdvancedScript: propTypes.bool,
  isSelected: propTypes.bool,
  moreDetailsIsOpen: propTypes.bool,
  onClickEditTrigger: propTypes.func,
  onClickScriptOptionsTrigger: propTypes.func,
  onClickToggleDetailsTrigger: propTypes.func,
  onHideCallbackOptionsTrigger: propTypes.func,
  onSelectScript: propTypes.func,
  productValidationValues: propTypes.object,
  script: propTypes.object,
  scriptId: propTypes.string,
  scriptOptionsIsOpen: propTypes.bool,
  showPrimaryFlag: propTypes.bool
};

ApplicationScriptCardContainer = connect(
  // mapStateToProps
  (state, ownProps) => {
    const { script } = ownProps;
    const scriptId = script.id;
    const activityCategories = getActivityCategoriesByActivityType(state);
    const activityType = getActivityTypeForScript(script);
    const activity = getActivityByUuid(state, script['activity-uuid']);
    return {
      activity,
      activityCategory: activityCategories[activityType],
      canManualScript: canManualScript(state),
      applicationSummary: getManageApplicationSummary(script),
      isAdvancedScript: isAdvancedScript(script),
      isSelected: getActiveScriptId(state) === scriptId,
      moreDetailsIsOpen: moreDetailsIsOpenForScriptId(state, scriptId),
      onSelectScript: ownProps.onSelectScript,
      productValidationValues: getProductValidationValues(state),
      script: script,
      scriptId: scriptId,
      scriptOptionsIsOpen: scriptOptionsIsOpenForScriptId(state, scriptId),
      showPrimaryFlag: isDisplayPrimaryScript(state, script)
    };
  },
  (dispatch, ownProps) => {
    return {
      onClickEditTrigger: ({ scriptId, uiLocation }) => {
        dispatch(editScript({ scriptId, uiLocation }));
      },
      onClickScriptOptionsTrigger: ({ scriptId, currentStatus }) => {
        ownProps.onSelectScript(scriptId);
        dispatch(requestPreferredDevice());
        dispatch(requestDriveDevices());
        dispatch(requestAffiliateList());
        dispatch(setScriptOptionsIsOpen({ scriptId, status: !currentStatus }));
      },
      onHideCallbackOptionsTrigger: ({ scriptId }) => {
        dispatch(setScriptOptionsIsOpen({ scriptId, status: false }));
      },
      onClickToggleDetailsTrigger: ({ scriptId, currentStatus }) => {
        dispatch(setMoreDetailsIsOpen({ scriptId, status: !currentStatus }));
      }
    };
  }
)(ApplicationScriptCardContainer);

export default ApplicationScriptCardContainer;
